import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { ImageListItemComponent } from "../../../../shared/components/image-list-item/image-list-item.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FileComponent } from "../forms/file/file.component";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { ActivatedRoute } from "@angular/router";
import { AssetService } from "../../../../services/api/asset/asset.service";
import { ASSET_TYPES } from "../../../../enums/assetTypes.enum";
import { GateComponent } from "../forms/gate/gate.component";
import { first, forkJoin, map } from "rxjs";
import { ComponentsListComponent } from "../components-list/components-list.component";
import { MediaService } from "../../../../services/api/media/media.service";
import { ConfirmationService, MessageService } from "@openng/optimus-ui/api";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { DocumentListItemComponent } from "../../../../shared/components/document-list-item/document-list-item.component";

import { HasPermissionDirective } from "../../../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { FileData } from "../../../../models/shared/fileData";
import { Image } from "../../../../models/ui/image";

import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { SimplebarAngularModule } from "simplebar-angular";
import { GateSystem } from "../../../../models/api/asset/gateSystem";
import { Media } from "../../../../models/api/media";
import { Protocol } from "../../../../models/ui/protocol";
import { HardwareConfigurationDialogComponent } from "../hardware-configuration-dialog/hardware-configuration-dialog.component";

@Component({
  standalone: true,
  selector: "app-gate-details",
  imports: [
    ImageListItemComponent,
    SvgIconComponent,
    TranslateModule,
    ProgressSpinnerModule,
    DocumentListItemComponent,
    HasPermissionDirective,
    TimeoutComponent,
    TooltipModule,
    SimplebarAngularModule,
  ],
  templateUrl: "./gate-details.component.html",
  styleUrl: "./gate-details.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class GateDetailsComponent implements OnInit {
  public loadingInfo = signal(false);
  public timeoutInfo = createTimeoutSignal(this.loadingInfo);

  public loadingMedia = signal(false);
  public timeoutMedia = createTimeoutSignal(this.loadingMedia);

  public images: Image[] = [];
  public translate = inject(TranslateService);
  private ref: DynamicDialogRef | null = null;
  private dialogService = inject(DialogService);
  private route = inject(ActivatedRoute);

  public gateSystem!: GateSystem;
  private assetService = inject(AssetService);
  private message = inject(MessageService);
  private confirmation = inject(ConfirmationService);
  private gateSystemId!: string;

  private mediaService = inject(MediaService);
  public protocols: WritableSignal<Protocol[]> = signal([]);
  public permissions = inject(PermissionService);
  public imagesOrderMap: Record<string, number> = {
    gate_inside: 0,
    gate_outside: 1,
    motor: 2,
    suspension: 3,
    control: 4,
    name_plate: 5,
  };

  ngOnInit() {
    this.route.params.subscribe((pathParams) => {
      if (pathParams["id"]) {
        const assetId = pathParams["id"] as string;
        this.gateSystemId = assetId;
        this.setGateSystem(this.gateSystemId);
      }
    });
  }

  /**
   * Gets inspection reports for the gate system with the given id
   * @param assetId the id of the gate system
   */
  private getProtocols(assetId: string | undefined) {
    if (assetId) {
      forkJoin([
        this.mediaService.getMedia(ASSET_TYPES.GATE_SYSTEM, this.gateSystemId, "SERVICE_FORM"),
        this.mediaService.getMedia(ASSET_TYPES.GATE_SYSTEM, this.gateSystemId, "INSPECTION_REPORT"),
      ])
        .pipe(map(([serviceForms, inspectionReports]) => [...serviceForms, ...inspectionReports]))
        .subscribe((protocols) => {
          const mapedProtocols = protocols.map((p) => {
            const typeKey =
              p.mediaType === "SERVICE_FORM" ? "common.entities.service_form" : "common.entities.inspection_report";
            return {
              id: p.id,
              label: typeKey,
              timestamp: p.timestamp?.created_at,
              url: p.url,
            };
          });
          this.protocols.set(mapedProtocols);
        });
    }
  }
  /**
   * Loads images for the gate system with the given id
   * @param assetId the id of the gate system
   */
  private getMedia(assetId: string | undefined) {
    if (assetId) {
      this.loadingMedia.set(true);
      this.images = [];
      this.mediaService
        .getMedia(ASSET_TYPES.GATE_SYSTEM, assetId, "GATE_IMAGES")
        .pipe(first())
        .subscribe((media: Media[]) => {
          if (media) {
            media.forEach((mediaFile: Media) => {
              const label = mediaFile.additionalInfo?.label;
              const link = mediaFile.url;
              const id = mediaFile.id;
              const timestamp = mediaFile.timestamp?.created_at;
              if (label && link && id && timestamp) {
                {
                  const index: number = this.imagesOrderMap[label];
                  if (index !== undefined) {
                    this.images[index] = {
                      link: link,
                      labelDisplay: this.checkLabel(label),
                      labelValue: label,
                      uploadTimestamp: timestamp,
                      id: id,
                    };
                  }
                }
              }
            });
            media.forEach((mediaFile: Media) => {
              const label = mediaFile.additionalInfo?.label;
              const link = mediaFile.url;
              const id = mediaFile.id;
              const timestamp = mediaFile.timestamp?.created_at;
              if (label && link && id && timestamp) {
                const index: number = this.imagesOrderMap[label];

                if (index === undefined) {
                  this.images.push({
                    link: link,
                    labelDisplay: this.checkLabel(label),
                    labelValue: label,
                    uploadTimestamp: timestamp,
                    id: id,
                  });
                }
              }
            });
            this.images = this.images.filter((image) => image);
            this.loadingMedia.set(false);
          }
        });
    }
  }

  /**
   * Translates a gate image label if it is a predefined gate image type,
   * otherwise returns the label as is.
   *
   * @param label the label to be translated
   * @returns the translated label or the original label
   */
  private checkLabel(label: string) {
    if (this.mediaService.isPredefindedGateImage(label)) {
      return this.translate.instant("gate_administration.file_upload_form.photo_types." + label);
    } else {
      return label;
    }
  }

  /**
   * Fetches the gate system and its media and protocols when the component is
   * initialized with a valid assetId.
   *
   * @param assetId the id of the gate system
   */
  private setGateSystem(assetId: string) {
    this.loadingInfo.set(true);
    this.loadingMedia.set(true);
    this.assetService
      .getAssetDetails(assetId, ASSET_TYPES.GATE_SYSTEM)
      .pipe(first())
      .subscribe({
        next: (gateSystem) => {
          this.loadingInfo.set(false);
          if (gateSystem) {
            this.gateSystem = gateSystem as GateSystem;
            if (this.gateSystem.id) {
              this.getMedia(this.gateSystem.id);
              this.getProtocols(this.gateSystem.id);
            }
          }
        },
      });
  }

  /**
   * Opens a dialog for editing a gate system.
   * The dialog is a `GateComponent` with the given gate system as data.
   * If the dialog is closed with success, the gate system is reloaded.
   */
  public onEditClick() {
    this.ref = this.dialogService.open(GateComponent, {
      header: this.translate.instant("gate_administration.dialog_title.edit.gate_systems"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { asset: this.gateSystem, actionType: "edit" },
      closable: true,
    });
    this.ref?.onClose.subscribe((success: boolean) => {
      if (success) {
        this.setGateSystem(this.gateSystemId);
      }
    });
  }

  /** Opens the hardware configuration dialog for the current gate system. */
  public onHardwareConfigurationClick(): void {
    if (!this.gateSystemId) {
      return;
    }

    this.ref = this.dialogService.open(HardwareConfigurationDialogComponent, {
      header: this.translate.instant("gate_administration.hardware_configuration.title"),
      modal: true,
      width: "70vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "85vw",
        "640px": "95vw",
      },
      data: { gateSystemId: this.gateSystemId },
      closable: true,
    });
  }

  /**
   * Opens the given image in a new browser tab.
   * @param image the image to be opened
   */
  public openImage(image: Image) {
    window.open(image.link, "_blank");
  }

  /**
   * Opens a confirmation dialog to delete the given image.
   * If confirmed, the image is deleted and the media list is reloaded.
   * @param event the event that triggered the deletion
   * @param image the image to be deleted
   */
  public deleteImage(event: Event, image: Image) {
    if (event.currentTarget) {
      this.confirmation.confirm({
        target: event.currentTarget,
        message: this.translate.instant("gate_administration.gate_details.image_delete_confirm"),
        key: "global",
        accept: () => {
          this.mediaService.deleteMedia(image.id).subscribe({
            next: () => {
              this.getMedia(this.gateSystemId);
              this.message.add({
                key: "global",
                severity: "success",
                summary: this.translate.instant("common.success"),
                detail: this.translate.instant("gate_administration.gate_details.image_deleted"),
                life: 4000,
              });
            },
          });
        },
      });
    }
  }

  /**
   * Opens the components list dialog for the currently selected gate system.
   * Does nothing if no gate system is selected.
   */
  public onListClick() {
    if (this.gateSystem) {
      this.ref = this.dialogService.open(ComponentsListComponent, {
        header: this.translate.instant("gate_administration.components_list.title"),
        modal: true,
        width: "50vw",
        contentStyle: { overflow: "auto" },
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { gateSystem: this.gateSystem },
        closable: true,
      });
    }
  }

  /**
   * Opens the file dialog for editing the given image or creating a new one.
   * If the dialog is closed with success, the media list is reloaded.
   * @param image the image to be edited or undefined for creating a new one
   */
  public openFileDialog(image?: Image) {
    const availableTypes = this.mediaService.getAvailableImageTypes(
      this.images.map((image) => image.labelValue),
      image?.labelValue
    );
    this.ref = this.dialogService.open(FileComponent, {
      header: this.translate.instant("gate_administration.gate_details.add_image"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        image,
        availableTypes,
      },
      closable: true,
    });
    this.ref?.onClose.subscribe({
      next: (fileData: FileData) => {
        if (fileData && this.gateSystem?.id) {
          if (fileData.fileId) {
            // In case file already exists, update it
            this.mediaService
              .editMedia(fileData.fileId, fileData.file, fileData.label)
              .pipe(first())
              .subscribe(() => {
                this.getMedia(this.gateSystemId);
              });
          } else {
            this.mediaService
              .uploadMedia(ASSET_TYPES.GATE_SYSTEM, this.gateSystem.id, "GATE_IMAGES", fileData.file, fileData.label)
              .pipe(first())
              .subscribe(() => {
                this.getMedia(this.gateSystemId);
              });
          }
        }
      },
    });
  }
}
